import { useState } from 'react';
import { Link } from 'react-router-dom';
import { CheckCircle2, Loader2, Mail, AlertCircle } from 'lucide-react';
import { subscribeToInsights } from '@/services/enquiryService';
import { Reveal } from '../Reveal';

const CONSENT_WORDING =
  'I agree to receive occasional practical business insights, guides and updates from Amden Group by email. I can unsubscribe at any time.';

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const utmFromLocation = () => {
  if (typeof window === 'undefined') return undefined;
  const params = new URLSearchParams(window.location.search);
  const utm = {};
  ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'].forEach((k) => {
    const v = params.get(k);
    if (v) utm[k] = v;
  });
  return Object.keys(utm).length ? utm : undefined;
};

// Section 8 — Insights updates sign-up. Genuinely stores subscriptions in
// the backend (CRM-ready structure, source "Insights Subscription") — no
// false success states. Consent is never preselected and its wording plus
// timestamp are recorded. Emails will carry an unsubscribe route when the
// email-marketing integration is added.
export const InsightsSubscribe = () => {
  const [form, setForm] = useState({ firstName: '', email: '', businessName: '', consent: false, website: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [error, setError] = useState('');

  const set = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: key === 'consent' ? e.target.checked : e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (form.firstName.trim().length < 1) {
      setError('Please enter your first name.');
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (!form.consent) {
      setError('Please confirm you are happy to receive insights updates from Amden Group.');
      return;
    }

    setStatus('submitting');
    try {
      await subscribeToInsights({
        firstName: form.firstName.trim(),
        email: form.email.trim(),
        businessName: form.businessName.trim() || undefined,
        consent: form.consent,
        consentWording: CONSENT_WORDING,
        source: 'Insights Subscription',
        pageUrl: typeof window !== 'undefined' ? window.location.href : undefined,
        utm: utmFromLocation(),
        website: form.website, // honeypot — real users never fill this
      });
      setStatus('success');
    } catch {
      setStatus('error');
      setError('Something went wrong while subscribing. Please try again in a moment.');
    }
  };

  return (
    <section aria-labelledby="subscribe-heading" data-testid="insights-subscribe" className="bg-white py-16 sm:py-24">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal>
          <div className="mx-auto max-w-3xl rounded-2xl border border-slate-200 bg-mist p-8 sm:p-12">
            <div className="flex items-start gap-4">
              <span className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-md bg-navy-900 sm:inline-flex">
                <Mail className="h-5 w-5 text-gold-500" aria-hidden="true" />
              </span>
              <div>
                <h2 id="subscribe-heading" data-testid="subscribe-heading" className="text-2xl font-extrabold tracking-tight text-navy-900 sm:text-3xl">
                  Get practical business insights
                </h2>
                <p className="mt-3 text-base leading-relaxed text-slate-600">
                  Receive occasional advice, guides and updates from Amden Group.
                </p>
              </div>
            </div>

            {status === 'success' ? (
              <div
                role="status"
                data-testid="subscribe-success"
                className="mt-8 flex items-start gap-3 rounded-lg border border-teal-200 bg-teal-50 p-5"
              >
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-teal-700" aria-hidden="true" />
                <div>
                  <p className="text-sm font-bold text-teal-900">You’re subscribed.</p>
                  <p className="mt-1 text-sm leading-relaxed text-teal-800">
                    Thank you, {form.firstName.trim()} — occasional practical insights will land in
                    your inbox. Every email will include a clear unsubscribe link.
                  </p>
                </div>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="mt-8" data-testid="subscribe-form">
                <div className="grid gap-4 sm:grid-cols-2">
                  <div>
                    <label htmlFor="sub-first-name" className="text-sm font-bold text-navy-900">
                      First name
                    </label>
                    <input
                      id="sub-first-name"
                      type="text"
                      autoComplete="given-name"
                      value={form.firstName}
                      onChange={set('firstName')}
                      data-testid="subscribe-first-name"
                      className="mt-1.5 h-11 w-full rounded-md border border-slate-300 bg-white px-3.5 text-sm text-navy-900 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1"
                    />
                  </div>
                  <div>
                    <label htmlFor="sub-email" className="text-sm font-bold text-navy-900">
                      Email address
                    </label>
                    <input
                      id="sub-email"
                      type="email"
                      autoComplete="email"
                      value={form.email}
                      onChange={set('email')}
                      data-testid="subscribe-email"
                      className="mt-1.5 h-11 w-full rounded-md border border-slate-300 bg-white px-3.5 text-sm text-navy-900 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1"
                    />
                  </div>
                  <div className="sm:col-span-2">
                    <label htmlFor="sub-business" className="text-sm font-bold text-navy-900">
                      Business name <span className="font-medium text-slate-400">(optional)</span>
                    </label>
                    <input
                      id="sub-business"
                      type="text"
                      autoComplete="organization"
                      value={form.businessName}
                      onChange={set('businessName')}
                      data-testid="subscribe-business-name"
                      className="mt-1.5 h-11 w-full rounded-md border border-slate-300 bg-white px-3.5 text-sm text-navy-900 shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1"
                    />
                  </div>
                </div>

                {/* Honeypot — invisible to real users, catches bots */}
                <div className="absolute left-[-9999px] top-auto h-px w-px overflow-hidden" aria-hidden="true">
                  <label htmlFor="sub-website">Website</label>
                  <input
                    id="sub-website"
                    type="text"
                    tabIndex={-1}
                    autoComplete="off"
                    value={form.website}
                    onChange={set('website')}
                  />
                </div>

                <div className="mt-5 flex items-start gap-3">
                  <input
                    id="sub-consent"
                    type="checkbox"
                    checked={form.consent}
                    onChange={set('consent')}
                    data-testid="subscribe-consent"
                    className="mt-1 h-4 w-4 rounded border-slate-300 text-navy-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-900 focus-visible:ring-offset-1"
                  />
                  <label htmlFor="sub-consent" className="text-sm leading-relaxed text-slate-600">
                    {CONSENT_WORDING} Read our{' '}
                    <Link to="/privacy-policy" className="font-bold text-navy-900 underline-offset-2 hover:underline">
                      privacy policy
                    </Link>
                    .
                  </label>
                </div>

                {error && (
                  <p role="alert" data-testid="subscribe-error" className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-red-700">
                    <AlertCircle className="h-4 w-4" aria-hidden="true" />
                    {error}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === 'submitting'}
                  data-testid="subscribe-submit-btn"
                  className="mt-6 inline-flex items-center justify-center gap-2 rounded-md bg-navy-900 px-7 py-3.5 text-sm font-bold text-white transition-colors hover:bg-navy-700 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {status === 'submitting' && <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />}
                  {status === 'submitting' ? 'Subscribing…' : 'Subscribe'}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
